import React, { useCallback, useState } from 'react';

import type { AiSuggestionsRightRailEntryPoint } from '@atlaskit/editor-common/analytics';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useLivePageModeFlag } from '@confluence/live-pages-features/entry-points/useLivePageModeFlag.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

import { SuggestionFilterValues } from '../suggestionsPanelFilters.ts';
import { useSuggestionsPanelFilters } from '../useSuggestionsPanelFilters.ts';
import { useSuggestionsSource } from '../useSuggestionsSource.ts';

import { SuggestionsFooter } from './SuggestionsFooter.tsx';
import { SuggestionsPanelContent } from './SuggestionsPanelContent.tsx';
import { SuggestionsPanelLayout } from './SuggestionsPanelLayout.tsx';

type SuggestionsPanelProps = {
	entryPoint?: AiSuggestionsRightRailEntryPoint;
};

const SuggestionsPanelComponent = ({
	entryPoint,
	flags,
}: SuggestionsPanelProps & WithFlagsProps): React.JSX.Element => {
	const { showLivePageModeFlag } = useLivePageModeFlag(flags);
	const announceLiveDocEditMode = useCallback(
		() => showLivePageModeFlag('edit'),
		[showLivePageModeFlag],
	);
	const source = useSuggestionsSource({ onSwitchLiveDocToEditMode: announceLiveDocEditMode });
	const { visibleSuggestions } = source;
	const [{ currentFilter }] = useSuggestionsPanelFilters();
	const editorActions =
		source.mode === 'editor' ? source.editorAPI?.aiSuggestions?.actions : undefined;
	const handleDiscardAll = useCallback(() => {
		if (currentFilter === SuggestionFilterValues.ALL) {
			editorActions?.discardAllSuggestions();
		} else {
			editorActions?.discardAllSuggestions(visibleSuggestions.map(({ id }) => id));
		}
	}, [editorActions, currentFilter, visibleSuggestions]);
	const isEditorPage = useIsEditorPage();
	const [isContinuingFromPublished, setIsContinuingFromPublished] = useState(false);
	if (!isContinuingFromPublished && source.mode === 'published' && isEditorPage) {
		setIsContinuingFromPublished(true);
	}
	return (
		<SuggestionsPanelLayout
			content={
				<SuggestionsPanelContent
					// Published suggestions are replaced by the editor's, so the content starts afresh.
					key={source.mode === 'published' ? 'published' : 'editor'}
					source={source}
					entryPoint={entryPoint}
					isStackEntranceSkipped={isContinuingFromPublished}
				/>
			}
			footer={
				<SuggestionsFooter
					visibleSuggestions={visibleSuggestions}
					onDiscardAll={source.mode === 'editor' ? handleDiscardAll : undefined}
				/>
			}
		/>
	);
};

export const SuggestionsPanel: React.ComponentType<SuggestionsPanelProps> =
	withFlags<SuggestionsPanelProps>(SuggestionsPanelComponent);
